/**
 * Integration Actions Service
 * 
 * Provides methods to invoke integration actions and check integration availability.
 * Used for UI-interactive integrations like Confluence document import.
 * 
 * @created 2025-12-26
 */

import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// Types for integration actions
export interface IntegrationAction {
  description: string;
  params: Record<string, {
    type: string;
    required: boolean;
    description: string;
  }>;
  returns: Record<string, {
    type: string;
    description: string;
  }>;
}

export interface IntegrationUICapabilities {
  documentImport?: boolean;
  policyImport?: boolean;
  search?: boolean;
}

export interface IntegrationActionsInfo {
  integrationId: string;
  actions: Record<string, IntegrationAction>;
  uiCapabilities: IntegrationUICapabilities;
}

export interface ActionResult<T = any> {
  success: boolean;
  result?: T;
  error?: string;
  logs?: Array<{
    level: string;
    message: string;
    timestamp?: string;
  }>;
}

// Confluence-specific types
export interface ConfluenceSearchResult {
  id: string;
  title: string;
  spaceKey: string;
  spaceName?: string;
  lastModified?: string;
  url?: string;
  excerpt?: string;
}

export interface ConfluenceSpace {
  id: number;
  key: string;
  name: string;
  type: string;
  status: string;
}

export interface ConfluenceExportResult {
  file: string; // Base64 encoded
  fileName: string;
  mimeType: string;
  pageTitle: string;
}

export interface ConfluencePreviewResult {
  html: string;
  title: string;
  pageId: string;
}

/**
 * Check if an integration is enabled for the current tenant
 */
export async function isIntegrationEnabled(integrationId: string): Promise<boolean> {
  try {
    const response = await deiterateAPI.GET(`/integrations/${integrationId}/actions`, {}, true);
    return response?.statusCode === 200;
  } catch (error) {
    console.log(`[IntegrationActions] Integration ${integrationId} not enabled:`, error);
    return false;
  }
}

/**
 * Get available actions for an integration
 */
export async function getIntegrationActions(integrationId: string): Promise<IntegrationActionsInfo | null> {
  try {
    const response = await deiterateAPI.GET(`/integrations/${integrationId}/actions`, {}, true);
    
    if (response?.statusCode !== 200) {
      return null;
    }
    
    return {
      integrationId: response.integrationId,
      actions: response.actions || {},
      uiCapabilities: response.uiCapabilities || {},
    };
  } catch (error) {
    console.error(`[IntegrationActions] Failed to get actions for ${integrationId}:`, error);
    return null;
  }
}

/**
 * Invoke an integration action
 */
export async function invokeAction<T = any>(
  integrationId: string,
  actionName: string,
  params: Record<string, any> = {}
): Promise<ActionResult<T>> {
  try {
    console.log(`[IntegrationActions] Invoking ${integrationId}/${actionName}`, params);
    
    const response = await deiterateAPI.POST(
      `/integrations/${integrationId}/actions/${actionName}`,
      { params },
      true
    );
    
    if (response?.success) {
      return {
        success: true,
        result: response.result as T,
        logs: response.logs,
      };
    }
    
    return {
      success: false,
      error: response?.error || 'Action failed',
      logs: response?.logs,
    };
  } catch (error: any) {
    console.error(`[IntegrationActions] Action ${actionName} failed:`, error);
    return {
      success: false,
      error: error.message || 'Failed to invoke action',
    };
  }
}

// ============================================
// Confluence-specific helper functions
// ============================================

/**
 * Check if Confluence integration is available
 */
export async function isConfluenceEnabled(): Promise<boolean> {
  return isIntegrationEnabled('confluence');
}

/**
 * Search Confluence pages
 */
export async function searchConfluencePages(
  query: string,
  options: { spaceKey?: string; limit?: number } = {}
): Promise<ActionResult<{ results: ConfluenceSearchResult[] }>> {
  return invokeAction('confluence', 'search', {
    query,
    ...options,
  });
}

/**
 * List Confluence spaces
 */
export async function listConfluenceSpaces(): Promise<ActionResult<{ spaces: ConfluenceSpace[] }>> {
  return invokeAction('confluence', 'spaces', {});
}

/**
 * Get Confluence page preview (HTML)
 */
export async function previewConfluencePage(
  pageId: string
): Promise<ActionResult<ConfluencePreviewResult>> {
  return invokeAction('confluence', 'preview', { pageId });
}

/**
 * Export Confluence page to file
 */
export async function exportConfluencePage(
  pageId: string,
  format: 'pdf' | 'html' | 'word' = 'pdf'
): Promise<ActionResult<ConfluenceExportResult>> {
  return invokeAction('confluence', 'export', { pageId, format });
}

/**
 * Test Confluence connectivity
 */
export async function testConfluenceConnection(): Promise<ActionResult<{
  success: boolean;
  message: string;
  spaces?: ConfluenceSpace[];
}>> {
  return invokeAction('confluence', 'test', {});
}

/**
 * Convert base64 file to File object
 */
export function base64ToFile(
  base64: string,
  fileName: string,
  mimeType: string
): File {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Export Confluence page and convert to File object
 */
export async function exportConfluencePageAsFile(
  pageId: string,
  format: 'pdf' | 'html' | 'word' = 'pdf'
): Promise<{ success: boolean; file?: File; error?: string }> {
  const result = await exportConfluencePage(pageId, format);
  
  if (!result.success || !result.result) {
    return {
      success: false,
      error: result.error || 'Export failed',
    };
  }
  
  const { file, fileName, mimeType } = result.result;
  const fileObject = base64ToFile(file, fileName, mimeType);
  
  return {
    success: true,
    file: fileObject,
  };
}

/**
 * Extract page ID from Confluence URL
 * Supports various URL formats:
 * - https://company.atlassian.net/wiki/spaces/SPACE/pages/123456/Page+Title
 * - https://company.atlassian.net/wiki/pages/viewpage.action?pageId=123456
 */
export function extractConfluencePageId(url: string): string | null {
  // Try to match /pages/{pageId}/ format
  const pagesMatch = url.match(/\/pages\/(\d+)/);
  if (pagesMatch) {
    return pagesMatch[1];
  }
  
  // Try to match pageId= query parameter
  const queryMatch = url.match(/pageId=(\d+)/);
  if (queryMatch) {
    return queryMatch[1];
  }
  
  return null;
}
